/**
 * CreateMessageForm - Form fields for creating a new message
 *
 * Reuses UI layout from the original MessageCreateForm with 2-column layout
 * Logic is driven by parent (CreateMessageModal) via props
 */

import React, { useContext, memo, useState, useRef, useEffect, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import Dropzone from 'react-dropzone';
import { useParams } from 'react-router-dom';

import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import LabelName from 'components/UI/LabelName/LabelName';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import CustomMessageScheduleOverlayPanel from 'components/CutomMessageScheduleOverlayPanel/CustomMessageScheduleOverlayPanel';
import EventSelect from 'components/UI/EventSelect/EventSelect';
import FirmUserSelect from 'components/UI/FirmUserSelect/FirmUserSelect';

import { useMessageContext } from '../../context';
import { UserDetailsContext } from 'context/userDetailsContext';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { ACCEPTED_FILE_TYPES } from '../../constants';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { convertToHtml } from 'utils/utility_functions/convertToHtml';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const MAX_FILES = 20;

function CreateMessageForm({
  onSubmit,
  attendeesList,
  files,
  addFiles,
  removeFile,
  getCaseClients,
  handleClose,
  isSubmitting,
  uploadPercentage,
  scheduleData,
  setScheduleData,
  fetchedExistingFiles,
}) {
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { id } = useParams();

  const { isGlobal, isFromClientPortal, clientPortalDashboard } = useMessageContext();
  const userContext = useContext(UserDetailsContext);
  const { checkSubPermission } = useUserDetailsContext();
  const { addToast } = useToast();

  const op = useRef(null);
  const modalContainerRef = useRef(null);
  const [showSchedulePanel, setShowSchedulePanel] = useState(false);
  const [panelPosition, setPanelPosition] = useState('below');
  const [filteredAttendeesList, setFilteredAttendeesList] = useState();

  const showCaseSelector = isGlobal || isFromClientPortal;
  const caseId = watch('caseId');

  // Filter attendees list for client portal (no groups)
  useEffect(() => {
    setFilteredAttendeesList(attendeesList?.filter((item) => item?.label !== 'Groups'));
  }, [attendeesList, isFromClientPortal]);

  const onChangeFile = (filesData) => {
    const totalFiles = [...files, ...filesData];
    if (totalFiles?.length > MAX_FILES) {
      addToast(false, toastConstant.toasterType.WARN, toastConstant.header.WARNINIG, toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED);
      return;
    }
    addFiles(filesData);
  };

  function MessageToOptionTemplate(e) {
    if (e?.res_type === 'user-group') {
      return <div>{e?.label}</div>;
    } else {
      const isClient = e.access_level === 'client';

      return (
        <div>
          {e?.firm_user_id && <i className="fas fa-user-tie me-2"></i>}
          {e?.label}{' '}
          {(isClient || !e.contact_id) && !e.email
            ? '( Email address not found )'
            : (isClient || e.contact_id) && !e.is_create_client_portal
            ? '(Disabled Lawft Access  )'
            : e?.status === 'ARCHIVED' && !e.contact_id
            ? ` (Inactive)`
            : ''}
        </div>
      );
    }
  }

  function checkOptionDisabled(index) {
    if ((index.contact_id || index.access_level === 'client') && index?.res_type !== 'user-group') {
      return index.email === '' || !index.is_create_client_portal ? true : false;
    }
    return false;
  }

  return (
    <div ref={modalContainerRef} style={{ position: 'relative', overflow: 'visible', minHeight: '400px' }}>
      <form onSubmit={handleSubmit(onSubmit)} className="F-size14 message-create-form">
        {/* Form Heading */}
        <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
          <div className="modal-heading F-size16 text-bold">Create New Message</div>
        </div>

        <div className="d-flex align-items-center">
          <div className="col-12">
            <div>
              {/* Case selection (global only) */}
              {showCaseSelector && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={true} htmlFor="caseId">
                      Case/Lead Name
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      className="w-100 input-shadow"
                      name="caseId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <CaseListDropDown
                          isClient={isFromClientPortal}
                          value={field.value}
                          onChange={(e) => {
                            if (field.value !== e) {
                              field.onChange(e);
                              if (e?.id) {
                                getCaseClients(e?.id);
                                setValue('messageTo', []);
                                setValue('existing_files', []);
                              }
                            }
                          }}
                          placeholder="By Case/Lead"
                          noOptionsMessage="No Cases/Leads Found"
                          name="case"
                          disabled={clientPortalDashboard}
                        />
                      )}
                    />
                    {errors.caseId && <InputErrorMessage>Please select a case/lead</InputErrorMessage>}
                  </div>
                </div>
              )}

              {/* Recipients */}
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={true} htmlFor="messageTo">
                    To
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="messageTo"
                    control={control}
                    rules={{
                      required: true,
                      validate: (selectedValues) => {
                        if (isFromClientPortal) {
                          if (!selectedValues || selectedValues?.length === 0) return false;
                          const firmUsers = (filteredAttendeesList || []).find((group) => group.label === 'Firm Users')?.items || [];
                          const selectedFirmUsers = firmUsers?.filter((firmUser) => selectedValues.includes(firmUser?.value));
                          return selectedFirmUsers?.length > 0 || 'At least one Firm User must be selected.';
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <MultiSelect
                        id={field.name}
                        value={attendeesList?.length > 0 ? field?.value : []}
                        placeholder="Select a Recipient"
                        onChange={(e) => field.onChange(e.value)}
                        display="chip"
                        className="input-shadow w-100"
                        filter
                        disabled={!caseId}
                        options={!isFromClientPortal ? attendeesList || [] : filteredAttendeesList || []}
                        optionLabel="label"
                        optionGroupLabel="label"
                        optionGroupChildren="items"
                        optionDisabled={(index) => checkOptionDisabled(index)}
                        itemTemplate={MessageToOptionTemplate}
                        emptyFilterMessage="No records found"
                        showSelectAll={false}
                      />
                    )}
                  />
                  {errors.messageTo?.type === 'required' ? (
                    <InputErrorMessage>Please select a Recipient</InputErrorMessage>
                  ) : errors.messageTo?.type === 'validate' && errors.messageTo?.message === 'At least one Firm User must be selected.' ? (
                    <InputErrorMessage>{errors.messageTo?.message}</InputErrorMessage>
                  ) : errors.messageTo?.type === 'validate' ? (
                    <InputErrorMessage>The contact you selected has no email address to receive messages</InputErrorMessage>
                  ) : null}
                </div>
              </div>

              {/* Subject */}
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={true} htmlFor="subject">
                    Subject
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: true }}
                    defaultValue=""
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="text"
                        id={field.name}
                        {...field}
                        placeholder="Subject"
                        className="w-100 input-shadow"
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.subject && <InputErrorMessage>Please enter subject name</InputErrorMessage>}
                </div>
              </div>

              {/* Message body */}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={true} htmlFor="message">
                    Message
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    control={control}
                    name="message"
                    className="shadow-small"
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <TextSnippetWrapper
                        type="editor"
                        value={sanitizeHtmlColorContent(value)}
                        style={{ height: '150px' }}
                        onChange={(e) => onChange(convertToHtml(e))}
                      />
                    )}
                  />
                  {errors?.message && <InputErrorMessage>Please enter your message</InputErrorMessage>}
                </div>
              </div>

              {/* Optional Section Heading */}
              <div className="my-3">
                <div className="col-md-12 col-12">
                  <h5 className="modal-heading F-size16 text-bold mb-0">Optional</h5>
                </div>
              </div>

              {/* File attachments */}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 mt-2">
                  <LabelName required={false} htmlFor="message">
                    Attachments
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Dropzone onDrop={onChangeFile} accept={ACCEPTED_FILE_TYPES}>
                    {({ getRootProps, getInputProps }) => (
                      <section>
                        <div {...getRootProps()}>
                          <input {...getInputProps()} />
                          <p className="text-center font-bold attachmnet-input input-shadow mb-0">
                            Click or drag file to this area to upload
                          </p>
                        </div>
                      </section>
                    )}
                  </Dropzone>
                  <ul>
                    {files &&
                      files.length > 0 &&
                      files.map((val, index) => (
                        <div className="d-flex mt-2" key={index}>
                          <i className="fas fa-paperclip me-2 mt-1"></i>
                          <li className="text-break">{val.name}</li>
                          <i className="icon-delete ms-2 mt-1 pointer" onClick={() => removeFile(val.path)}></i>
                        </div>
                      ))}
                  </ul>
                </div>
              </div>

              {/* Event & Task attachments */}
              {!isFromClientPortal && (
                <>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12">
                      <LabelName required={false} htmlFor="event_attachment">
                        Event
                      </LabelName>
                    </div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="event_attachment"
                        control={control}
                        render={({ field }) => (
                          <EventSelect
                            id={field.name}
                            name={field.name}
                            value={field.value ?? null}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Select an Event"
                            calendarType="event"
                            timezone={userContext?.userDetails?.timezone}
                            isDisabled={!caseId}
                            isMulti
                            isClearable={false}
                            caseId={caseId?.id || id}
                            className="input-shadow w-100"
                            key={caseId}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {checkSubPermission('task_management') && (
                    <div className="d-flex align-items-center py-2 flex-wrap">
                      <div className="col-md-4 col-12">
                        <LabelName required={false} htmlFor="task_attachment">
                          Task
                        </LabelName>
                      </div>
                      <div className="col-md-8 col-12">
                        <Controller
                          name="task_attachment"
                          control={control}
                          render={({ field }) => (
                            <EventSelect
                              id={field.name}
                              name={field.name}
                              value={field.value ?? null}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              placeholder="Select a Task"
                              calendarType="task"
                              timezone={userContext?.userDetails?.timezone}
                              isDisabled={!caseId}
                              isMulti
                              isClearable={false}
                              caseId={caseId?.id || id}
                              className="input-shadow w-100"
                              key={caseId}
                              noOptionsMessage={() => 'No Tasks Found'}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Attach Existing Files */}
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={false} htmlFor="existing_files">
                    Attach Existing Files
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="existing_files"
                    control={control}
                    render={({ field: { name, value, onChange } }) => (
                      <MultiSelect
                        options={fetchedExistingFiles?.map((item) => ({
                          label: item.display_name,
                          value: {
                            file_path: item?.file_path,
                            size: item?.size,
                            display_name: item?.display_name,
                            file_name: item?.file_name,
                            file_type: item?.file_type,
                            created_by: item?.created_by_details,
                            is_new_file: true,
                            sk: item?.sk,
                            pk: item?.pk,
                          },
                        }))}
                        className="input-shadow w-100"
                        placeholder="Select Existing Files"
                        panelClassName={fetchedExistingFiles?.length > 0 ? '' : 'no-records'}
                        display="chip"
                        value={Array.isArray(value) ? value : []}
                        filter={fetchedExistingFiles?.length > 0}
                        onChange={(e) => onChange(e?.value)}
                        disabled={!caseId}
                        optionLabel="label"
                        optionValue="value"
                        optionDisabled={(index) => index?.value === 'no-records-found'}
                        showClear
                        resetFilterOnHide
                        id={name}
                        emptyFilterMessage="No records found"
                        showSelectAll={false}
                        panelFooterTemplate={
                          fetchedExistingFiles?.length > 0
                            ? null
                            : () => <div className="text-center black-500">No existing files found</div>
                        }
                      />
                    )}
                  />
                </div>
              </div>

              {/* Resolution and Assign To */}
              {!isFromClientPortal && (
                <>
                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12">
                      <LabelName required={false} htmlFor="resolution_status">
                        Need Resolution
                      </LabelName>
                    </div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="resolution_status"
                        control={control}
                        defaultValue={false}
                        render={({ field: { name, ref, onChange, value }, fieldState }) => (
                          <Checkbox
                            inputId={name}
                            ref={ref}
                            onChange={(e) => onChange(e.checked)}
                            checked={value}
                            className={{ 'p-invalid': fieldState.invalid }}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="d-flex align-items-center py-2 flex-wrap">
                    <div className="col-md-4 col-12">
                      <LabelName required={false} htmlFor="assign_to">
                        Assign to Firm User {watch('resolution_status') && <span className="alert-star"> *</span>}
                      </LabelName>
                    </div>
                    <div className="col-md-8 col-12">
                      <Controller
                        name="assign_to"
                        control={control}
                        rules={{ required: watch('resolution_status') ? 'Assignee is Required' : false }}
                        render={({ field }) => (
                          <FirmUserSelect
                            id={field.name}
                            name={field.name}
                            value={field.value ?? null}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder="Select a Firm User"
                            isMulti
                            isClearable={false}
                            className="w-100 input-shadow"
                            menuPlacement="top"
                          />
                        )}
                      />
                      {watch('resolution_status') && errors?.assign_to && <InputErrorMessage>Assign To is Required</InputErrorMessage>}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className="footer-btn d-flex justify-content-between">
              <div className="d-flex">
                {uploadPercentage > 0 && <div className="upload-percentage me-2">Uploading Attachments: {uploadPercentage}%</div>}
              </div>
              <div className="scheduled-button-group d-flex position-relative overflow-visible">
                <Button type="button" label="Cancel" className="p-button p-button-secondary outline me-2" onClick={handleClose} />
                {isFromClientPortal ? (
                  <div className="schedule-button-group">
                    <Button
                      type="submit"
                      label="Send"
                      className="p-button p-component p-button-secondary"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    />
                  </div>
                ) : (
                  <div className="schedule-button-group">
                    <Button
                      type="submit"
                      label={scheduleData ? 'Schedule' : 'Send'}
                      className="p-button p-button-secondary schedule-main-btn"
                      disabled={isSubmitting}
                      loading={isSubmitting}
                    />
                    <div
                      className={`schedule-dropdown-btn ${showSchedulePanel ? 'panel-open' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        setShowSchedulePanel((prevState) => {
                          const newState = !prevState;

                          if (newState) {
                            setTimeout(() => {
                              const buttonRect = e.target.getBoundingClientRect();
                              const modalRect = modalContainerRef.current?.getBoundingClientRect();
                              if (modalRect && buttonRect.bottom + 400 > modalRect.bottom) {
                                setPanelPosition('above');
                              } else {
                                setPanelPosition('below');
                              }
                            }, 0);
                          }

                          return newState;
                        });
                      }}
                    >
                      <div className="schedule-separator"></div>
                      <i className="pi pi-chevron-down schedule-chevron"></i>
                    </div>
                  </div>
                )}
                <CustomMessageScheduleOverlayPanel
                  ref={op}
                  visible={showSchedulePanel}
                  onHide={() => setShowSchedulePanel(false)}
                  style={{
                    position: 'absolute',
                    top: panelPosition === 'above' ? 'auto' : '100%',
                    bottom: panelPosition === 'above' ? '100%' : 'auto',
                    right: '0',
                    zIndex: 1000,
                    marginTop: panelPosition === 'above' ? '0' : '8px',
                    marginBottom: panelPosition === 'above' ? '8px' : '0',
                  }}
                  scheduleData={scheduleData}
                  setScheduleData={setScheduleData}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Scheduled datetime display */}
        {scheduleData?.datetime && (
          <div className="mt-2 f-size13 text-hint justify-content-end d-flex">
            Scheduled to send:{' '}
            {convertDateFromApiToUserTimezone(
              scheduleData?.datetime,
              userContext?.userDetails?.timezone,
              constants.month_date_year_time_12_format
            )}
          </div>
        )}
      </form>
    </div>
  );
}

export default memo(CreateMessageForm);
